import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { getSellerAbsoluteUrl } from "@/lib/routing/portal-urls";
import { getRestaurantAbsoluteUrl } from "@/lib/routing/portal-urls";
import { useCreateStoreImpersonationSessionMutation } from "@/shared/services/useCreateStoreImpersonationSessionMutation";

/**
 * Opening a seller session, shared by the button and the row menu.
 *
 * The blank tab is opened BEFORE the await, synchronously inside the click, or
 * the browser treats the later `window.open` as unrequested and blocks it. That
 * ordering is the whole reason this is a hook rather than two call sites.
 */
export function useImpersonateStore(
  storeId: string,
  locale: string,
  portal: "seller" | "restaurant" = "seller",
) {
  const t = useTranslations();
  const { mutateAsync: createImpersonationSession } =
    useCreateStoreImpersonationSessionMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const impersonate = async () => {
    setError(null);
    const openedTab = window.open("about:blank", "_blank");
    if (!openedTab) {
      setError(t(keys.errors.generic));
      return;
    }
    openedTab.document.write("<title>Opening seller portal...</title>");

    setIsSubmitting(true);
    try {
      const payload = await createImpersonationSession({ storeId });
      openedTab.location.assign(
        portal === "restaurant"
          ? getRestaurantAbsoluteUrl(locale)
          : getSellerAbsoluteUrl(locale, payload.storeSlug),
      );
    } catch (caughtError) {
      openedTab.close();
      setError(
        caughtError instanceof ApiJsonClientError
          ? caughtError.message || t(keys.errors.generic)
          : t(keys.errors.generic),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return { impersonate, isSubmitting, error };
}
